import React, { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import axiosClient from '../axios-client';
import '../styles/components/coverLetter.scss'; // Import the SCSS file
import { ResumeText } from './resumeTailoring';  // Import the shared interface

import { mainUrl } from '../constant';
import axios from 'axios';
import { addUtmParameters } from '../utils/utm';
import { useApplicationModal } from '../contexts/ApplicationModalContext';
import ApplicationConfirmModal from './ApplicationConfirmModal';
import { motion, AnimatePresence } from 'framer-motion';


interface JobData {
  _id: string;
  title: string;
  original_title?: string;
  company: string;
  location: string;
  location_hub?: string;
  summary?: string;
  qualifications?: string[];
  preferredQualifications?: string[];
  responsibilities?: string[];
  environment?: string;
  skills?: string | string[]; // Allow skills to be a string or an array
  salary?: string;
  experience_level?: string;
  jobType?: string;
  tailored_flag?: string;
  days_to_live?: object;
  job_url?: string;
  raw?: string;
}

interface CoverLetterProps {
  job: JobData;
  resumeText: ResumeText;
  hasBeenTailored: boolean;
  setHasBeenTailored: (value: boolean) => void;
  sessionContent: string | null;
  setSessionContent: (content: string | null) => void;
  setShowCoverLetterTab?: (value: boolean) => void;
}



const CoverLetter: React.FC<CoverLetterProps> = ({
  job,
  resumeText,
  hasBeenTailored,
  setHasBeenTailored,
  sessionContent,
  setSessionContent,
  setShowCoverLetterTab,
}) => {
  const [coverLetter, setCoverLetter] = useState(
    sessionContent ? JSON.parse(sessionContent) : { content: '' }
  );
  const [loading, setLoading] = useState(false);


  const [showButtons, setShowButtons] = useState(false);
  const [showAnimation, setShowAnimation] = useState(false);
  
  // One-shot generation state
  const isGenerating = useRef(false);
  const hasAttemptedGeneration = useRef(false);
  const { setModalData } = useApplicationModal();
  const [hasLeftForExternalSite, setHasLeftForExternalSite] = useState(false);



  // Add new paragraph reveal logic with proper spacing
  const paragraphs = useMemo(() => {
    if (!coverLetter.content) return [];
    return coverLetter.content.split(/\n\s*\n/).map((p: string) => p.trim()).filter(Boolean);
  }, [coverLetter.content]);

  const generateCoverLetter = useCallback(async () => {
    // Prevent multiple calls
    if (hasBeenTailored || isGenerating.current || hasAttemptedGeneration.current) {
      return;
    }

    hasAttemptedGeneration.current = true;
    isGenerating.current = true;
    setLoading(true);
    setShowAnimation(true);

    try {
      // Industry-grade job description extraction (same as resume component)
      let description = '';
      
      if (typeof job === 'string') {
        description = job;
      } else if (job && typeof job === 'object') {
        const jobParts = [];
        
        if (job.title) jobParts.push(`Job Title: ${job.title}`);
        if (job.company) jobParts.push(`Company: ${job.company}`);
        if (job.summary) jobParts.push(`About the Job: ${job.summary}`);
        if (job.qualifications && job.qualifications.length > 0) {
          jobParts.push(`Requirements: ${job.qualifications.join('; ')}`);
        }
        if (job.responsibilities && job.responsibilities.length > 0) {
          jobParts.push(`Responsibilities: ${job.responsibilities.join('; ')}`);
        }
        if (job.skills) {
          const skillsText = Array.isArray(job.skills) 
            ? job.skills.join(', ') 
            : job.skills;
          jobParts.push(`Skills: ${skillsText}`);
        }
        if (job.experience_level) jobParts.push(`Experience Level: ${job.experience_level}`);
        
        description = jobParts.join('\n\n');
      } else {
        description = JSON.stringify(job);
      }

      const response = await axiosClient.post('builder/cover-letter', {
        jobDescription: description,
        userDetails: JSON.stringify(resumeText),
      }, {
        timeout: 60000
      });

      setCoverLetter(response.data);
      setHasBeenTailored(true);
      setShowButtons(true);
      setSessionContent(JSON.stringify(response.data));
      setShowCoverLetterTab?.(true);
      
    } catch (error) {
      console.error('Cover letter generation failed:', error);
      setCoverLetter({ 
        content: 'Failed to generate cover letter. Please try again.'
      });
      // Reset on error so user can retry
      hasAttemptedGeneration.current = false;
    } finally {
      isGenerating.current = false;
      setLoading(false);
      setTimeout(() => setShowAnimation(false), 1000);
    }
  }, [hasBeenTailored, job, resumeText, setHasBeenTailored, setShowCoverLetterTab]);

  // Initialize from session content when available
  useEffect(() => {
    if (sessionContent) {
      setCoverLetter(JSON.parse(sessionContent));
      setShowButtons(true);
      setLoading(false);
      return;
    }
  }, [sessionContent]);

  // Simple useEffect - one shot generation
  useEffect(() => {
    if (!hasBeenTailored && !sessionContent && job?._id) {
      generateCoverLetter();
    }
  }, [hasBeenTailored, sessionContent, job?._id, generateCoverLetter]);

  // Handle user edits
  const handleEdit = (e: React.FocusEvent<HTMLDivElement>) => {
    // Get all paragraph elements
    const paragraphElements = e.currentTarget.getElementsByTagName('p');
    
    // Convert to array and map to text content
    const paragraphTexts = Array.from(paragraphElements).map(p => p.textContent?.trim() || '');
    
    // Join with double newlines to preserve paragraph structure
    const formattedContent = paragraphTexts.join('\n\n');
    
    const updatedCoverLetter = {
      ...coverLetter,
      content: formattedContent
    };
    setCoverLetter(updatedCoverLetter);
    setShowButtons(true);
    setSessionContent(JSON.stringify(updatedCoverLetter));

    // Store the current content in localStorage with the job ID
    localStorage.setItem(`currentCoverLetter_${job._id}`, formattedContent);
    console.log('Cover letter content updated:', {
      length: formattedContent.length,
      preview: formattedContent.substring(0, 100) + '...',
      paragraphs: paragraphTexts.length,
      lineBreaks: (formattedContent.match(/\n/g) || []).length
    });
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isGenerating.current = false;
      hasAttemptedGeneration.current = false;
    };
  }, []);

  // Define handleTextSelection with useCallback BEFORE it's used in useEffect  
  const handleTextSelection = useCallback(() => {
    // Text selection handling can be added here if needed for future features
  }, []);

  // Fix the event listener to use the properly defined handleTextSelection
  useEffect(() => {
    document.addEventListener('mouseup', handleTextSelection);
    return () => {
      document.removeEventListener('mouseup', handleTextSelection);
    };
  }, [handleTextSelection]);



  const handleDownload = async () => {
    const user = JSON.parse(String(localStorage.getItem("user")));
    if (coverLetter.content) {
      try {
        const response = await axiosClient.post('/pdf/generate-coverletter-pdf', { 'coverLetter': coverLetter.content });
        if (response.data && response.data.downloadUrl) {
          // Download without opening a new tab: fetch the PDF as a blob then trigger a hidden anchor click.
          const pdfBlobResponse = await axios.get(
            `${mainUrl}${response.data.downloadUrl}`,
            { responseType: 'blob' }
          );

          const blob = new Blob([pdfBlobResponse.data], { type: 'application/pdf' });
          const url = URL.createObjectURL(blob);

          const link = document.createElement('a');
          link.href = url;
          link.download = `${user.firstName}_${user.lastName}_coverletter.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          setTimeout(() => URL.revokeObjectURL(url), 60000);

          setShowButtons(true);
        } else {
          throw new Error('Download URL not received from server');
        }
      } catch (error: any) {
        console.error('Error generating PDF:', error);
        if (axios.isAxiosError(error) && error.response) {
          console.error('Error response:', error.response.data);
          alert(`Error: ${error.response.data.message || 'Unknown error'}`);
        } else {
          alert('An error occurred while generating the PDF. Please try again.');
        }
      }
    }
  }

  // Add effect to handle window focus
  useEffect(() => {
    const handleWindowFocus = () => {
      if (hasLeftForExternalSite) {
        setModalData({ 
          showModal: true, 
          jobTitle: job.title, 
          companyName: job.company,
          jobId: job._id
        });
        setHasLeftForExternalSite(false);
      }
    };

    window.addEventListener('focus', handleWindowFocus);
    return () => window.removeEventListener('focus', handleWindowFocus);
  }, [hasLeftForExternalSite, job.title, job.company, job._id, setModalData]);

  const handleApply = (e: React.MouseEvent) => {
    e.preventDefault();
    if (job.job_url) {
      // Get the content with preserved formatting
      const contentDiv = document.querySelector('.editable-content');
      const paragraphElements = contentDiv?.getElementsByTagName('p') || [];
      const paragraphTexts = Array.from(paragraphElements).map(p => p.textContent?.trim() || '');
      const formattedContent = paragraphTexts.join('\n\n');
      
      localStorage.setItem(`currentCoverLetter_${job._id}`, formattedContent);
      
      console.log('Saving cover letter before apply:', {
        jobId: job._id,
        hasContent: !!formattedContent,
        contentLength: formattedContent.length,
        paragraphs: paragraphTexts.length
      });

      // Store complete application data
      const applicationData = {
        resumeContent: resumeText,
        coverLetterContent: formattedContent,
        jobId: job._id
      };

      localStorage.setItem('currentApplication', JSON.stringify(applicationData));

      setHasLeftForExternalSite(true);
      window.open(addUtmParameters(job.job_url), '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div className="resume-layout-wrapper">
      <div className="left-container">
        <div className={`cover-letter-container ${loading ? 'loading' : ''}`}>
          <div className="cover-letter-content">
            <div className="content">
              {loading ? (
                <div className="loading-placeholder">
                  <div className="placeholder-line short"></div>
                  <div className="placeholder-line medium"></div>
                  <div className="placeholder-line long"></div>
                  <div className="placeholder-line"></div>
                  <div className="placeholder-line medium"></div>
                  <div className="placeholder-line short"></div>
                  <div className="placeholder-line"></div>
                </div>
              ) : (
                <div
                  contentEditable={!loading}
                  onBlur={handleEdit}
                  onSelect={handleTextSelection}
                  suppressContentEditableWarning={true}
                  className="editable-content"
                  style={{ 
                    whiteSpace: 'pre-wrap',
                    position: 'relative',
                    zIndex: 1200 
                  }}
                >
                  <AnimatePresence>
                    {showAnimation ? (
                      paragraphs.map((paragraph: string, index: number) => (
                        <motion.p
                          key={index}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{
                            duration: 0.5,
                            delay: index * 0.2,
                            ease: "easeOut"
                          }}
                          style={{ marginBottom: '1rem' }}
                        >
                          {paragraph}
                        </motion.p>
                      ))
                    ) : (
                      paragraphs.map((paragraph: string, index: number) => (
                        <p key={index} style={{ marginBottom: '1rem' }}>
                          {paragraph}
                        </p>
                      ))
                    )}
                  </AnimatePresence>
                </div>
              )}
            </div>
          </div>
         {/* {selection && <HoveringButton />} }
          {showSuggestions && <SuggestionsCard /> */}
        </div>
      </div>
      <div className="right-container">
        <div className='bottom-0 absolute w-11/12 flex flex-col gap-3'>
          {showButtons && (
            <>
              <a
                href={job.job_url}
                onClick={handleApply}
                className="external-link"
              >
                Apply Externally <svg className="external-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M21 3V9H19V6.414L11.207 14.207L9.793 12.793L17.586 5H15V3H21Z" fill="currentColor"/>
                  <path d="M19 19H5V5H12V3H5C3.897 3 3 3.897 3 5V19C3 20.103 3.897 21 5 21H19C20.103 21 21 20.103 21 19V12H19V19Z" fill="currentColor"/>
                </svg>
              </a>
              <button className='action-btn primary' onClick={handleDownload}>
                Download PDF
              </button>
            </>
          )}
        </div>
      </div>
      {hasLeftForExternalSite && (
        <div className="modal-backdrop">
          <ApplicationConfirmModal />
        </div>
      )}
    </div>
  );
};

export default CoverLetter;